import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiClient, prepareApiRequest } from './client';
import { newIdempotencyKey } from './engagement-requirements';
import { CitationSchema as KbCitationSchema } from './vendor-knowledge';

/**
 * Engagement -> Discovery -> Process maps (T-0189, over the T-0180 and
 * T-0182 API). Contract: `apps/api/src/routes/process-maps.ts` and
 * `apps/api/src/services/process-maps.ts` / `map-chat.ts`, all under
 * `/api/v1/engagements/:engagement_id/discovery/process-maps`.
 *
 *   - A map is data: lanes, steps and flows. The server generates the BPMN
 *     (`bpmn_xml`) and counts the metrics; the screen never counts.
 *   - Every change is a change set: proposed, checked by code, then applied
 *     (a new version) or discarded. Undo writes a new version too.
 *   - Writes take an `Idempotency-Key`, one per confirmed action.
 *   - T-0208 (E13-F09, over T-0204/T-0205): future-state maps are the same
 *     maps with `kind = 'future'` and a vendor product. Their change tags,
 *     vendor citations, requirements met, removed steps, newer vendor
 *     information and counts all come from the server, as it computes them.
 */

const StepTypeSchema = z.enum([
  'start_event',
  'end_event',
  'task',
  'user_task',
  'manual_task',
  'service_task',
  'exclusive_gateway',
]);
export type MapStepType = z.infer<typeof StepTypeSchema>;

const ValidationStateSchema = z.enum([
  'none',
  'requested',
  'validated',
  'changes_requested',
  'superseded',
]);
export type MapValidationState = z.infer<typeof ValidationStateSchema>;

const UserRefSchema = z.object({ id: z.string(), name: z.string() }).nullable();

const ProductRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  vendor: z.object({ id: z.string(), name: z.string() }),
});
export type MapProductRef = z.infer<typeof ProductRefSchema>;

/** One future-state map of a process (E13-F09 §3, `GET /processes`). */
const FutureMapSummarySchema = z.object({
  map_id: z.string(),
  vendor_product: ProductRefSchema,
  /** Null until the AI draft writes version 1. */
  latest_version_no: z.number().nullable(),
  validation_state: ValidationStateSchema.nullable(),
  /** Citations on the latest version whose items have newer vendor information. */
  vendor_information_changes: z.number(),
  drawn_against_version_no: z.number().nullable(),
  /** A newer current-state version is validated: a redraw is offered, never made. */
  newer_current_validated: z.boolean(),
  /** The AI draft or redraw running for this map, or the last one that failed. */
  draft: z
    .object({
      run_id: z.string(),
      status: z.enum(['drafting', 'failed']),
      error_code: z.string().nullable(),
      message: z.string().nullable(),
    })
    .nullable()
    .optional(),
});
export type FutureMapSummary = z.infer<typeof FutureMapSummarySchema>;

const ProcessSchema = z.object({
  process_id: z.string(),
  name: z.string(),
  area: z.object({ id: z.string(), label: z.string() }),
  confirmed_evidence: z.number(),
  ready: z.boolean(),
  map: z
    .object({
      id: z.string(),
      latest_version_no: z.number(),
      validation_state: ValidationStateSchema,
      unconfirmed_steps: z.number(),
    })
    .nullable(),
  /** T-0181 — an AI draft of a process with no map yet: running, or failed with the reason. */
  draft: z
    .object({
      run_id: z.string(),
      status: z.enum(['drafting', 'failed']),
      error_code: z.string().nullable(),
      message: z.string().nullable(),
    })
    .nullable()
    .optional(),
  /** T-0204 — the newest validated version of the current map; a future state is drawn from it. */
  current_validated_version_no: z.number().nullable().optional(),
  /** T-0204 — the process's future-state maps, one per vendor product. */
  future_maps: z.array(FutureMapSummarySchema).optional(),
});
export type MapProcess = z.infer<typeof ProcessSchema>;

const CitationSchema = z.object({
  kind: z.enum(['discovery_evidence', 'client_validation']),
  evidence_id: z.string().optional(),
  evidence_number: z.string().nullable().optional(),
  text: z.string().nullable().optional(),
  evidence_status: z.string().optional(),
  validation: z
    .object({
      id: z.string(),
      validated_by: z.string().nullable(),
      validated_at: z.string().nullable(),
    })
    .optional(),
});
export type MapCitation = z.infer<typeof CitationSchema>;

/** A future step against today (ADR-020 T2): derived by code from step keys. */
const FutureTagSchema = z.enum(['unchanged', 'changed', 'automated', 'new']);
export type FutureTag = z.infer<typeof FutureTagSchema>;

/**
 * A knowledge item a future step cites, pinned to that version (rule 25):
 * its citation exactly as the knowledge base renders it for a consultant,
 * the vendor-confidential excerpt included (rule 21).
 */
const VendorCitationSchema = z.object({
  item_id: z.string(),
  number: z.string().nullable(),
  kind: z.string(),
  statement: z.string().nullable(),
  citation: KbCitationSchema.nullable(),
  state: z.string(),
});
export type MapVendorCitation = z.infer<typeof VendorCitationSchema>;

const StepSchema = z.object({
  key: z.string(),
  type: StepTypeSchema,
  lane: z.string(),
  name: z.string(),
  system: z.string().nullable(),
  origin: z.enum(['ai_draft', 'chat', 'manual', 'canvas']),
  origin_user: UserRefSchema,
  origin_message_id: z.string().nullable(),
  confirmation: z.enum(['confirmed', 'unconfirmed']).nullable(),
  needs_attention: z.boolean(),
  citations: z.array(CitationSchema),
  pain_points: z.array(z.object({ id: z.string(), number: z.string().nullable() })),
  /**
   * T-0286 (E25-design §4): the AI's pending question for a step nobody has
   * described, with why. Null once the step is described or validated; an
   * API before T-0286 sends none.
   */
  gap_question: z
    .object({ ai_proposal_id: z.string(), text: z.string(), why: z.string() })
    .nullable()
    .optional(),
  // Future-state maps only (T-0204): all computed by the server.
  tag: FutureTagSchema.optional(),
  workaround: z.boolean().optional(),
  is_assumption: z.boolean().optional(),
  vendor_citations: z.array(VendorCitationSchema).optional(),
  requirements_met: z
    .array(
      z.object({ requirement_id: z.string(), number: z.string(), edited_after_load: z.boolean() }),
    )
    .optional(),
});
export type MapStepDto = z.infer<typeof StepSchema>;

const MetricsSchema = z.object({
  steps_people_do: z.number(),
  steps_on_paper: z.number(),
  steps_system_does: z.number(),
  handoffs: z.number(),
  unconfirmed_steps: z.number(),
  pain_points: z.number(),
});
export type MapMetricsDto = z.infer<typeof MetricsSchema>;

/** `futureMapMetrics` (E13-F09 §2): counted by code on every read, never stored (rule 26). */
const FutureMetricsSchema = z.object({
  people: z.number(),
  paper: z.number(),
  system: z.number(),
  handoffs: z.number(),
  vendor_cited: z.number(),
  assumptions: z.number(),
  workarounds: z.number(),
  removed: z.number(),
  pain_points_on_removed_or_changed: z.number(),
});
export type FutureMetricsDto = z.infer<typeof FutureMetricsSchema>;

const StepRefSchema = z.object({
  key: z.string(),
  name: z.string(),
  type: StepTypeSchema,
  lane: z.string(),
  lane_name: z.string(),
});
export type MapStepRef = z.infer<typeof StepRefSchema>;

const VendorInfoChangeSchema = z.object({
  step_key: z.string(),
  item_id: z.string(),
  item_number: z.string().nullable(),
  state: z.enum(['superseded', 'retired', 'flagged']),
  successor_id: z.string().optional(),
  successor_number: z.string().nullable().optional(),
  flag_reason: z.string().optional(),
});
export type VendorInfoChange = z.infer<typeof VendorInfoChangeSchema>;

const MapSchema = z.object({
  map: z.object({
    id: z.string(),
    process_id: z.string(),
    area_id: z.string(),
    name: z.string(),
    kind: z.enum(['current', 'future']),
    latest_version_no: z.number(),
  }),
  version: z.object({
    no: z.number(),
    source: z.string(),
    created_by: UserRefSchema,
    created_at: z.string(),
    validation_state: ValidationStateSchema,
    validation: z
      .object({ person: z.string().nullable(), outcome: z.string(), answered_at: z.string() })
      .nullable(),
  }),
  lanes: z.array(z.object({ key: z.string(), name: z.string(), is_system: z.boolean() })),
  steps: z.array(StepSchema),
  flows: z.array(
    z.object({
      key: z.string(),
      from: z.string(),
      to: z.string(),
      label: z.string().nullable(),
      is_exception: z.boolean(),
    }),
  ),
  bpmn_xml: z.string(),
  metrics: z.union([MetricsSchema, FutureMetricsSchema]),
  provenance_line: z.string(),
  // Future-state maps only (T-0204).
  vendor_product: ProductRefSchema.optional(),
  current_state_version_no: z.number().optional(),
  removed_steps: z.array(StepRefSchema).optional(),
  vendor_information_changes: z.array(VendorInfoChangeSchema).optional(),
});
export type ProcessMapDto = z.infer<typeof MapSchema>;

/** A current map's counts; a future map's are `FutureMetricsDto`. */
export function isCurrentMetrics(m: ProcessMapDto['metrics'] | undefined): m is MapMetricsDto {
  return m !== undefined && 'steps_people_do' in m;
}

/** `GET /future-products?process_id`: products to draw a future state with, counted by code. */
const FutureProductSchema = z.object({
  product_id: z.string(),
  name: z.string(),
  vendor: z.object({ id: z.string(), name: z.string() }),
  confirmed_items: z.number(),
  answers_to_engagement_requirements: z.number(),
  future_map_id: z.string().nullable().optional(),
});
export type FutureProduct = z.infer<typeof FutureProductSchema>;

const CompareStepSchema = z.object({
  key: z.string(),
  name: z.string(),
  type: StepTypeSchema,
  lane: z.string(),
});

/** `GET /{map_id}/compare`: a future version beside the current state it was drawn against. */
const CompareSchema = z.object({
  today: z.object({
    version_no: z.number(),
    bpmn_xml: z.string(),
    steps: z.array(CompareStepSchema.extend({ tag: z.enum(['removed', 'changed', 'unchanged']) })),
  }),
  future: z.object({
    version_no: z.number(),
    bpmn_xml: z.string(),
    steps: z.array(
      CompareStepSchema.extend({
        tag: FutureTagSchema,
        workaround: z.boolean(),
        is_assumption: z.boolean(),
      }),
    ),
  }),
  pairs: z.array(z.object({ current_key: z.string(), future_key: z.string() })),
});
export type MapCompare = z.infer<typeof CompareSchema>;

/** `GET /compare-table?process_id`: today and each product's latest future version. */
const CompareTableSchema = z.object({
  process_id: z.string(),
  rows: z.array(
    z.discriminatedUnion('kind', [
      z.object({
        kind: z.literal('today'),
        map_id: z.string(),
        version_no: z.number(),
        validation_state: ValidationStateSchema,
        metrics: z.object({
          people: z.number(),
          paper: z.number(),
          system: z.number(),
          handoffs: z.number(),
          pain_points: z.number(),
        }),
      }),
      z.object({
        kind: z.literal('future'),
        map_id: z.string(),
        vendor_product: ProductRefSchema,
        version_no: z.number(),
        drawn_against_version_no: z.number(),
        metrics: FutureMetricsSchema,
      }),
    ]),
  ),
});
export type MapCompareTable = z.infer<typeof CompareTableSchema>;

const VersionSchema = z.object({
  no: z.number(),
  source: z.string(),
  created_by: UserRefSchema,
  created_at: z.string(),
  change_summary: z.string(),
  validation_state: ValidationStateSchema,
});
export type MapVersion = z.infer<typeof VersionSchema>;

const PreviewSchema = z.object({
  added: z.array(z.string()),
  removed: z.array(z.string()),
  renamed: z.array(z.string()),
  moved: z.array(z.string()),
  flows_changed: z.array(z.string()),
  lanes_changed: z.array(z.string()),
  citations_changed: z.array(z.string()),
});
export type MapChangePreview = z.infer<typeof PreviewSchema>;

/** POST …/change-sets: code's verdict on a change set, stored either way. */
const ChangeSetSchema = z.object({
  change_set_id: z.string(),
  valid: z.boolean(),
  operations: z.array(z.record(z.unknown())),
  preview: PreviewSchema.optional(),
  summary: z.array(z.string()).optional(),
  invalid_reason: z.string().optional(),
  operation_index: z.number().nullable().optional(),
  check: z.string().optional(),
});
export type ChangeSetVerdict = z.infer<typeof ChangeSetSchema>;

const ChatChangeSetSchema = z.object({
  id: z.string(),
  status: z.enum(['proposed', 'applied', 'discarded', 'stale']),
  valid: z.boolean(),
  invalid_reason: z.string().optional(),
  operation_index: z.number().nullable().optional(),
  check: z.string().optional(),
  operations: z.array(z.record(z.unknown())),
  preview: PreviewSchema.optional(),
  summary: z.array(z.string()).optional(),
  applied_version_no: z.number().nullable().optional(),
});
export type ChatChangeSet = z.infer<typeof ChatChangeSetSchema>;

const FactsSchema = z
  .object({
    count: z.number(),
    steps: z.array(z.object({ key: z.string(), name: z.string() }).passthrough()),
  })
  .passthrough();

const ChatMessageSchema = z.object({
  id: z.string(),
  role: z.enum(['user', 'assistant']),
  kind: z.string(),
  text: z.string(),
  author: UserRefSchema,
  created_at: z.string(),
  reply_to: z.string().optional(),
  change_set: ChatChangeSetSchema.optional(),
  facts: FactsSchema.optional(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const ChatThreadSchema = z.object({
  thread_id: z.string().nullable(),
  messages: z.array(ChatMessageSchema),
});
export type ChatThread = z.infer<typeof ChatThreadSchema>;

export const ChatReplySchema = z.object({
  message_id: z.string(),
  reply: z.object({ id: z.string(), text: z.string() }),
  kind: z.enum(['change_request', 'question', 'cannot']),
  change_set: ChatChangeSetSchema.optional(),
});

const ValidationRequestSchema = z.object({
  link_id: z.string(),
  expires_at: z.string(),
  version_no: z.number(),
  person: z.object({ id: z.string(), name: z.string() }),
});

const PersonSchema = z
  .object({
    id: z.string(),
    full_name: z.string(),
    title: z.string().nullable(),
    email: z.string().nullable(),
    status: z.string(),
    merged_into: z.unknown().nullable().optional(),
  })
  .passthrough();
export type Validator = { id: string; name: string; title: string | null };

const EvidenceItemSchema = z
  .object({
    id: z.string(),
    number: z.string().nullable(),
    status: z.string(),
    text: z.string(),
  })
  .passthrough();
export type PickableEvidence = z.infer<typeof EvidenceItemSchema>;

const PainPointSchema = z
  .object({
    id: z.string(),
    number: z.string().nullable(),
    text: z.string(),
    status: z.string(),
    area_ids: z.array(z.string()),
  })
  .passthrough();
export type MapPainPoint = z.infer<typeof PainPointSchema>;

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

function discovery(engagementId: string): string {
  return `/api/v1/engagements/${encodeURIComponent(engagementId)}/discovery`;
}
function maps(engagementId: string): string {
  return `${discovery(engagementId)}/process-maps`;
}
function one(engagementId: string, mapId: string): string {
  return `${maps(engagementId)}/${encodeURIComponent(mapId)}`;
}

export const getMapProcesses = (engagementId: string) =>
  apiClient(`${maps(engagementId)}/processes`, z.array(ProcessSchema));

/**
 * T-0181 — draft a process's current-state map with AI, from its area's
 * confirmed evidence first (Decisions 54.30, 54.31). The worker writes
 * version 1; until then the process shows the draft running.
 */
export const startMapDraft = (engagementId: string, processId: string) =>
  apiClient(maps(engagementId), z.object({ map_id: z.string(), run_id: z.string() }), {
    method: 'POST',
    body: { process_id: processId },
    idempotencyKey: newIdempotencyKey(),
  });

/** T-0204 — the products a future state can be drawn with, those with items in the area first. */
export const getFutureProducts = (engagementId: string, processId: string) =>
  apiClient(`${maps(engagementId)}/future-products`, z.array(FutureProductSchema), {
    query: { process_id: processId },
  });

/**
 * T-0205 — draw a process's future state with a product (Decision 62): the
 * map is created once per product and AI drafts version 1 from the
 * validated current state. Refused until the current state is validated.
 */
export const startFutureDraft = (engagementId: string, processId: string, productId: string) =>
  apiClient(`${maps(engagementId)}/future`, z.object({ map_id: z.string(), run_id: z.string() }), {
    method: 'POST',
    body: { process_id: processId, vendor_product_id: productId },
    idempotencyKey: newIdempotencyKey(),
  });

/** T-0205 — draw a future map again from the newest validated current state, as its next version. */
export const redrawFutureMap = (engagementId: string, mapId: string, baseVersionNo: number) =>
  apiClient(`${one(engagementId, mapId)}:redraw`, z.object({ run_id: z.string() }), {
    method: 'POST',
    body: { base_version_no: baseVersionNo },
    idempotencyKey: newIdempotencyKey(),
  });

export const getMapCompare = (engagementId: string, mapId: string, version?: number) =>
  apiClient(`${one(engagementId, mapId)}/compare`, CompareSchema, { query: { version } });

export const getCompareTable = (engagementId: string, processId: string) =>
  apiClient(`${maps(engagementId)}/compare-table`, CompareTableSchema, {
    query: { process_id: processId },
  });

export const getProcessMap = (engagementId: string, mapId: string, version?: number) =>
  apiClient(one(engagementId, mapId), MapSchema, { query: { version } });

/**
 * T-0286 — ask AI to draft a question for each step of the map nobody has
 * described. A run already going for the map is joined. Refused (409) when
 * every step is described or validated.
 */
export const proposeStepQuestions = (engagementId: string, mapId: string) =>
  apiClient(
    `${one(engagementId, mapId)}/step-questions:propose`,
    z.object({ run_id: z.string(), joined: z.boolean().optional() }).passthrough(),
    { method: 'POST', body: {}, idempotencyKey: newIdempotencyKey() },
  );

export const getMapVersions = (engagementId: string, mapId: string) =>
  apiClient(`${one(engagementId, mapId)}/versions`, z.array(VersionSchema));

export const getMapChat = (engagementId: string, mapId: string) =>
  apiClient(`${one(engagementId, mapId)}/chat`, ChatThreadSchema);

export interface ProposeBody {
  base_version_no: number;
  origin: 'canvas' | 'manual';
  operations: ReadonlyArray<Record<string, unknown>>;
}

/** A change set from the canvas or the step panel. Nothing changes until it is applied. */
export const proposeChangeSet = (engagementId: string, mapId: string, body: ProposeBody) =>
  apiClient(`${one(engagementId, mapId)}/change-sets`, ChangeSetSchema, {
    method: 'POST',
    body,
    idempotencyKey: newIdempotencyKey(),
  });

export const applyChangeSet = (engagementId: string, mapId: string, changeSetId: string) =>
  apiClient(
    `${one(engagementId, mapId)}/change-sets/${encodeURIComponent(changeSetId)}:apply`,
    z.object({ version_no: z.number() }),
    { method: 'POST', idempotencyKey: newIdempotencyKey() },
  );

export const discardChangeSet = (engagementId: string, mapId: string, changeSetId: string) =>
  apiClient(
    `${one(engagementId, mapId)}/change-sets/${encodeURIComponent(changeSetId)}:discard`,
    z.object({ status: z.string() }),
    { method: 'POST', idempotencyKey: newIdempotencyKey() },
  );

/** Undo: a new version restoring an earlier one. Version numbers only go up. */
export const undoMap = (
  engagementId: string,
  mapId: string,
  body: { to_version_no: number; base_version_no: number },
) =>
  apiClient(`${one(engagementId, mapId)}:undo`, z.object({ version_no: z.number() }), {
    method: 'POST',
    body,
    idempotencyKey: newIdempotencyKey(),
  });

/**
 * A chat message: the consultant's words, or (future maps, rule 25) a
 * redraft of the steps with newer vendor information, whose text the server
 * writes. Either way the reply is a change set that changes nothing until
 * it is applied.
 */
export type MapChatBody =
  | { text: string; base_version_no: number }
  | { redraft_affected: true; base_version_no: number };

export const sendMapChat = (engagementId: string, mapId: string, body: MapChatBody) =>
  apiClient(`${one(engagementId, mapId)}/chat/messages`, ChatReplySchema, {
    method: 'POST',
    body,
    idempotencyKey: newIdempotencyKey(),
  });

export const requestMapValidation = (
  engagementId: string,
  mapId: string,
  versionNo: number,
  personId: string,
) =>
  apiClient(
    `${one(engagementId, mapId)}/versions/${versionNo}:request-validation`,
    ValidationRequestSchema,
    { method: 'POST', body: { person_id: personId }, idempotencyKey: newIdempotencyKey() },
  );

/** Anyone among the client's people with an email, not retired or merged away (Decision 54.33). */
export async function getValidators(engagementId: string): Promise<Validator[]> {
  const out = await apiClient(
    `${discovery(engagementId)}/people`,
    z.object({ items: z.array(PersonSchema) }),
  );
  return out.items
    .filter((p) => p.email && p.email.trim() !== '' && p.status !== 'retired' && !p.merged_into)
    .map((p) => ({ id: p.id, name: p.full_name, title: p.title }));
}

/** Confirmed evidence to link to a step (only confirmed evidence can be cited, rule 21). */
export async function searchConfirmedEvidence(
  engagementId: string,
  query: { area_id?: string; q?: string },
): Promise<PickableEvidence[]> {
  const out = await apiClient(
    `${discovery(engagementId)}/evidence`,
    z.object({ items: z.array(EvidenceItemSchema) }),
    {
      query: {
        status: 'confirmed',
        area_id: query.area_id,
        q: query.q && query.q.trim() !== '' ? query.q.trim() : undefined,
        limit: 25,
      },
    },
  );
  return out.items;
}

/** The engagement's confirmed pain points (T-0178), to name them on steps and link them. */
export const getConfirmedPainPoints = (engagementId: string) =>
  apiClient(`${discovery(engagementId)}/pain-points`, z.array(PainPointSchema), {
    query: { status: 'confirmed' },
  });

/** `filename="…"` from a Content-Disposition header. */
export function fileNameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      // fall through to the plain name
    }
  }
  const plain = /filename\s*=\s*"([^"]+)"/i.exec(header) ?? /filename\s*=\s*([^;]+)/i.exec(header);
  return plain?.[1]?.trim() || null;
}

/**
 * One version as a standard BPMN 2.0 file. A browser navigation never sends
 * the bearer header, so the file is fetched and handed to the caller.
 */
export async function downloadMapBpmn(
  engagementId: string,
  mapId: string,
  versionNo: number,
  fallbackName = `process-map-v${versionNo}.bpmn`,
): Promise<{ blob: Blob; fileName: string }> {
  const path = `${one(engagementId, mapId)}/versions/${versionNo}/bpmn`;
  const { url, headers, correlationId } = await prepareApiRequest(path, {
    headers: { Accept: 'application/xml' },
  });
  let res: Response;
  try {
    res = await fetch(url, { method: 'GET', headers });
  } catch (err) {
    throw new ApiError(
      0,
      'network',
      err instanceof Error ? err.message : 'Network error.',
      correlationId,
    );
  }
  if (!res.ok) {
    let body: { error?: { code?: string; message?: string; details?: Record<string, unknown> } };
    try {
      body = (await res.json()) as typeof body;
    } catch {
      body = {};
    }
    throw new ApiError(
      res.status,
      body.error?.code ?? 'internal',
      body.error?.message ?? `The download failed with status ${res.status}.`,
      correlationId,
      body.error?.details,
    );
  }
  return {
    blob: await res.blob(),
    fileName: fileNameFromDisposition(res.headers.get('content-disposition')) ?? fallbackName,
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const processMapKeys = {
  all: (engagementId: string) => ['process-maps', engagementId] as const,
  processes: (engagementId: string) => ['process-maps', engagementId, 'processes'] as const,
  map: (engagementId: string, mapId: string, version: number | null) =>
    ['process-maps', engagementId, 'map', mapId, version ?? 'latest'] as const,
  versions: (engagementId: string, mapId: string) =>
    ['process-maps', engagementId, 'versions', mapId] as const,
  chat: (engagementId: string, mapId: string) =>
    ['process-maps', engagementId, 'chat', mapId] as const,
  validators: (engagementId: string) => ['process-maps', engagementId, 'validators'] as const,
  painPoints: (engagementId: string) => ['process-maps', engagementId, 'pain-points'] as const,
  futureProducts: (engagementId: string, processId: string) =>
    ['process-maps', engagementId, 'future-products', processId] as const,
  compare: (engagementId: string, mapId: string, version: number | null) =>
    ['process-maps', engagementId, 'map', mapId, 'compare', version ?? 'latest'] as const,
  compareTable: (engagementId: string, processId: string) =>
    ['process-maps', engagementId, 'compare-table', processId] as const,
};

export function usePainPointsQuery(engagementId: string, enabled: boolean) {
  return useQuery({
    queryKey: processMapKeys.painPoints(engagementId),
    queryFn: () => getConfirmedPainPoints(engagementId),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useMapProcessesQuery(engagementId: string, enabled = true) {
  return useQuery({
    queryKey: processMapKeys.processes(engagementId),
    queryFn: () => getMapProcesses(engagementId),
    enabled,
    staleTime: 15_000,
    retry: false,
    // While AI drafts a map (current or future), look again until it is there.
    refetchInterval: (q) =>
      (q.state.data ?? []).some(
        (p) =>
          p.draft?.status === 'drafting' ||
          (p.future_maps ?? []).some((f) => f.draft?.status === 'drafting'),
      )
        ? 4_000
        : false,
  });
}

/**
 * One map version; `version` null is the latest. With `poll` the read
 * repeats every few seconds (while AI drafts the questions of its steps).
 */
export function useProcessMapQuery(
  engagementId: string,
  mapId: string,
  version: number | null,
  enabled = true,
  poll = false,
) {
  return useQuery({
    queryKey: processMapKeys.map(engagementId, mapId, version),
    queryFn: () => getProcessMap(engagementId, mapId, version ?? undefined),
    staleTime: version === null ? 5_000 : 60_000,
    retry: false,
    enabled,
    refetchInterval: poll ? 4_000 : false,
  });
}

/** Start (or join) the step question run of a map. The caller reads the map again for a while. */
export function useProposeStepQuestions(engagementId: string) {
  return useMutation({
    mutationFn: (mapId: string) => proposeStepQuestions(engagementId, mapId),
  });
}

export function useFutureProductsQuery(engagementId: string, processId: string, enabled: boolean) {
  return useQuery({
    queryKey: processMapKeys.futureProducts(engagementId, processId),
    queryFn: () => getFutureProducts(engagementId, processId),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/** A future version beside today; `version` null is the latest. */
export function useMapCompareQuery(
  engagementId: string,
  mapId: string,
  version: number | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: processMapKeys.compare(engagementId, mapId, version),
    queryFn: () => getMapCompare(engagementId, mapId, version ?? undefined),
    enabled,
    staleTime: 5_000,
    retry: false,
  });
}

export function useCompareTableQuery(engagementId: string, processId: string, enabled: boolean) {
  return useQuery({
    queryKey: processMapKeys.compareTable(engagementId, processId),
    queryFn: () => getCompareTable(engagementId, processId),
    enabled,
    staleTime: 5_000,
    retry: false,
  });
}

export function useMapVersionsQuery(engagementId: string, mapId: string) {
  return useQuery({
    queryKey: processMapKeys.versions(engagementId, mapId),
    queryFn: () => getMapVersions(engagementId, mapId),
    staleTime: 5_000,
    retry: false,
  });
}

export function useMapChatQuery(engagementId: string, mapId: string) {
  return useQuery({
    queryKey: processMapKeys.chat(engagementId, mapId),
    queryFn: () => getMapChat(engagementId, mapId),
    staleTime: 5_000,
    retry: false,
  });
}

export function useValidatorsQuery(engagementId: string, enabled: boolean) {
  return useQuery({
    queryKey: processMapKeys.validators(engagementId),
    queryFn: () => getValidators(engagementId),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/** After any write to a map: its versions, the latest version, the chat and the process list. */
export function useRefreshMap(engagementId: string, mapId: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['process-maps', engagementId, 'map', mapId] }),
      qc.invalidateQueries({ queryKey: processMapKeys.versions(engagementId, mapId) }),
      qc.invalidateQueries({ queryKey: processMapKeys.chat(engagementId, mapId) }),
      qc.invalidateQueries({ queryKey: processMapKeys.processes(engagementId) }),
      qc.invalidateQueries({ queryKey: ['process-maps', engagementId, 'compare-table'] }),
    ]);
}

export function useSendMapChatMutation(engagementId: string, mapId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MapChatBody) => sendMapChat(engagementId, mapId, body),
    onSettled: () => qc.invalidateQueries({ queryKey: processMapKeys.chat(engagementId, mapId) }),
  });
}
